import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { match as Match } from "react-router-dom";
import { injectIntl, IntlShape } from "react-intl";
import { toast } from "react-toastify";

import Table, { ALL_FILTER, IFilterField } from "../../../../components/Table";
import { GroundGraphqlContextStore } from "../../../../lib/ground-aws-graphql-core";
import PageSubheader from "../../../../components/PageSubheader";
import overrideClasses from "../../../../utils/overrideClasses";
import { TableChangeParams } from "../../../../components/Table/types";
import { Location } from "history";
import { EnumPermissionEntity } from "../../../../lib/ground-aws-graphql-core/api/graphql/types";
import {
  EnumBookingFrom,
  EnumPaths,
  navTo,
} from "../../../../utils/navigation";
import Dropdown, {
  DropdownValues,
} from "../../../../components/Tailwind/Dropdown";
import images from "../../../../images";
import {
  Quote,
  SearchableQuotesFilterInput,
} from "../../../../lib/ground-aws-graphql-core/models/Api/Quotes";
import history from "../../../../history";
import {
  DEFAULT_LIMIT,
  displayDayDDMMYYYY_HHMM,
} from "../../../../utils/config";
import DropdownQuoteStatus from "../../../../components/Tailwind/DropdownQuoteStatus";
import { EnumFilterFields } from "../../../../utils/filter";
import { EnumQuoteStatus } from "../../../../utils/quote";

interface Props {
  match: Match<{ cid: string }>;
  intl: IntlShape;
  location: Location<{
    pageIndex: number;
    limit: number;
    type: string;
    filters: IFilterField<{
      code: string;
      id: string;
      name: string;
    }>[];
  }>;
}

const ListQuotes = (props: Props) => {
  const { match, intl, location } = props;
  const [loading, setLoading] = useState(false);
  const [initialParams, setInitialParams] = useState<TableChangeParams | null>(
    location?.state
  );

  const listQuotes = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.listQuotes
  );

  const quotes = GroundGraphqlContextStore.useStoreState(
    s => s.quote.quotes.items
  );

  const total = GroundGraphqlContextStore.useStoreState(
    s => s.quote.quotes.total
  );

  const center = GroundGraphqlContextStore.useStoreState(s => s.center.center);

  const centerTimezone = GroundGraphqlContextStore.useStoreState(
    state => state.center.centerTimezone
  );

  const convertToOrder = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quote.convertToOrder
  );

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = (
    pageIndex = 0,
    loader = true,
    limit = DEFAULT_LIMIT,
    filters?: IFilterField<any>[]
  ) => {
    if (loader) {
      setLoading(true);
    }

    const filter: SearchableQuotesFilterInput = {};
    if (filters?.length) {
      filters.forEach(f => {
        if (
          f.type === EnumFilterFields[EnumFilterFields.STATUS] &&
          f.value?.value !== undefined &&
          f.value.value !== ALL_FILTER
        ) {
          filter.status = { eq: EnumQuoteStatus[f.value.value] };
        }

        if (
          f.type === EnumFilterFields[EnumFilterFields.CLIENT_NAME] &&
          f.value
        ) {
          filter.lastname = { like: f.value };
        }
      });
    }

    if (loader) setLoading(true);

    Promise.all([
      listQuotes({
        buildingId: match.params.cid,
        offset: pageIndex * limit,
        limit,
        filter,
      }),
    ]).finally(() => setLoading(false));
  };

  const tableHead = [
    "general.ref",
    "page.list.quotes.table.head.client",
    "general.date",
    "general.expires_at",
    "general.status",
    "page.list.bookings.order",
    "general.actions",
  ];

  const tableBody = quotes?.map((el: Quote) => {
    const {
      id,
      ref,
      created_at,
      global_order,
      user,
      quote_request,
      next_status,
      expires_at,
      can_convert_to_order,
    } = el;

    const editQuoteLink = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.QUOTES}/${id}/${EnumPaths.EDIT_MODE}`;

    const lastName = user.last_name || "";
    const firstName = user.first_name || "";

    const dropdownValues: DropdownValues[] = can_convert_to_order
      ? [
          {
            id: "convert-to-order",
            label: "page.list.quotes.convert.to.order",
            icon: images.edit,
            onClick: () => onConvertToOrderClick(el),
          },
        ]
      : global_order && global_order?.id
      ? [
          {
            id: "go-to-order",
            label: "page.list.bookings.show.order",
            icon: images.edit,
            onClick: () => onViewOrderClick(global_order?.id),
          },
        ]
      : [];

    if (quote_request && quote_request.id) {
      const link = `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.QUOTE_REQUESTS}/${quote_request.id}/${EnumPaths.EDIT_MODE}`;

      dropdownValues.push({
        id: "quote-request",
        label: "page.list.quotes.quote.request",
        icon: images.edit,
        onClick: () => onViewQuoteRequestClick(quote_request.id, link),
      });
    }

    const quoteElements = [
      {
        element: <div className="flex flex-col items-start">{ref}</div>,
        onCellClick: () => onQuoteClick(id, editQuoteLink),
      },
      {
        element: `${lastName} ${firstName}`,
        onCellClick: () => onQuoteClick(id, editQuoteLink),
      },
      {
        element: displayDayDDMMYYYY_HHMM(created_at, centerTimezone),
        onCellClick: () => onQuoteClick(id, editQuoteLink),
      },
      {
        element: displayDayDDMMYYYY_HHMM(expires_at, centerTimezone),
        onCellClick: () => onQuoteClick(id, editQuoteLink),
      },
      {
        element: (
          <DropdownQuoteStatus
            quote={{ ...el, can_download_pdf: false }}
            quoteStatus={[...next_status, el.status]}
            center={center}
            intl={intl}
            className="items-center"
            fromQuoteDetails={false}
          />
        ),
      },
      {
        element: (
          <div className="flex justify-center">
            {global_order && global_order.id && (
              <img src={images.checked} alt="quote-ok" />
            )}
          </div>
        ),
        onCellClick: () => onQuoteClick(id, editQuoteLink),
      },
      {
        element: (
          <Dropdown values={dropdownValues} dataCy={`quote-dropdown-${id}`} />
        ),
      },
    ];

    return {
      id: el.id.toString(),
      rowElements: quoteElements,
    };
  });

  const onQuoteClick = (id: number, editLink: string) => {
    history.push(editLink, initialParams);
  };

  const onViewQuoteRequestClick = (id: number, editLink: string) => {
    history.push(editLink, initialParams);
  };

  const onConvertToOrderClick = (quote: Quote) => {
    setLoading(true);
    convertToOrder({ id: quote.id })
      .then(() => {
        toast(
          intl.formatMessage({
            id: "page.quote.convert.to.order.success",
          }),
          {
            type: "success",
          }
        );
      })
      .catch(() => {
        toast(
          intl.formatMessage({
            id: "page.quote.convert.to.order.error",
          }),
          {
            type: "error",
          }
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const onViewOrderClick = (id: string) => {
    history.push({
      pathname: `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.GLOBAL_ORDERS}/${id}`,
      state: { from: EnumBookingFrom.QUOTE_LIST },
    });
  };

  const handleOnChange = (changeParams: TableChangeParams) => {
    const { pageIndex, limit, filters } = changeParams;

    // set initial params
    setInitialParams(changeParams);

    // fetch quote requests using filters
    fetchData(pageIndex, false, limit, filters);
  };

  const handleCreateQuote = () => {
    history.push(
      navTo(
        `${EnumPaths.BOOKINGS}/${EnumPaths.BOOK_AVAILABILITY}`,
        match.params.cid
      ),
      { from: EnumBookingFrom.QUOTE_LIST }
    );
  };

  return !center ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <Helmet>
        <title>
          {intl.formatMessage({
            id: "page.list.quotes.document.title",
          })}
        </title>
      </Helmet>

      <PageSubheader
        title="page.list.quotes.title"
        nbOfResults={total}
        buttonRightTitle={`page.list.quotes.create.quote`}
        buttonRightAction={() => handleCreateQuote()}
        buttonRightId={`page.list.quotes.create.quote`}
      />

      <Table
        initialParams={initialParams}
        head={tableHead}
        body={tableBody}
        limitChoiceEnabled
        noDataText="page.list.quotes.empty"
        onChange={handleOnChange}
        paginationTotal={total}
        paginationLimit={DEFAULT_LIMIT}
        permissionEntity={EnumPermissionEntity.QUOTE}
        useFilterBlock
        filterBlockTitle="page.list.quotes.filter.title"
        loading={loading}
        setLoading={setLoading}
        className="px-8"
      />
    </>
  );
};

export default injectIntl(ListQuotes);
